import { useEffect, useState } from "react";

// Per-user dashboard widget visibility. Users can hide entire
// widget categories from Settings → Dashboard widgets. The list
// here IS the source of truth: adding a new widget type to the
// dashboard means adding a row here so it shows up in the toggle
// panel. The dashboard calls widgetCategoryFromId() to figure out
// which category a given widget id belongs to.
export const WIDGET_CATEGORIES: Array<{
  id: string;
  label: string;
  description: string;
}> = [
  {
    id: "myschedule",
    label: "My schedule",
    description: "Your own next-2-weeks grid (plus forwarded officers' schedules).",
  },
  {
    id: "notifications",
    label: "Notifications",
    description: "Recent in-app notifications feed.",
  },
  {
    id: "understaffed",
    label: "Understaffed shifts",
    description: "Shifts that fall below min-staffing in the next 2 weeks.",
  },
  {
    id: "overstaffed",
    label: "Overstaffed shifts",
    description: "Shifts carrying more officers than the roster calls for.",
  },
  {
    id: "pendingTimeOff",
    label: "Pending time off",
    description: "Time-off requests waiting for a supervisor decision.",
  },
  {
    id: "pendingTraining",
    label: "Pending training",
    description: "Training requests waiting for a supervisor decision.",
  },
  {
    id: "coverageRequests",
    label: "Coverage requests",
    description: "Officer-initiated coverage swaps and OT requests waiting for review.",
  },
];

const LS_KEY = "dashboard.hiddenWidgetTypes";
const EVENT = "dashboard.hiddenWidgetTypes.changed";

// Maps a widget id (from the dashboard's allFlatWidgets pool) onto
// one of WIDGET_CATEGORIES.id. The id formats the dashboard uses
// today: "notifications" for the inbox; "self:<empId>" and
// "fwd:<empId>" for schedule widgets; "<deptId or all>:<name>"
// for every per-dept widget. Anything unrecognised keeps its own
// id so it stays visible by default even if the toggle panel
// doesn't list it.
export function widgetCategoryFromId(id: string): string {
  if (id === "notifications") return "notifications";
  if (id.startsWith("self:") || id.startsWith("fwd:")) return "myschedule";
  const idx = id.lastIndexOf(":");
  return idx === -1 ? id : id.substring(idx + 1);
}

export function getHiddenCategories(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function writeHiddenCategories(ids: Set<string>) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(Array.from(ids)));
    window.dispatchEvent(new Event(EVENT));
  } catch {
    /* localStorage may throw in private mode — ignore */
  }
}

// Hook: read + write the hidden-category set with cross-component
// sync via a custom event (same tab) and `storage` (other tabs).
export function useHiddenWidgetCategories(): [Set<string>, (next: Set<string>) => void] {
  const [state, setState] = useState<Set<string>>(() => getHiddenCategories());
  useEffect(() => {
    function onChange() { setState(getHiddenCategories()); }
    window.addEventListener(EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  function set(next: Set<string>) {
    writeHiddenCategories(next);
    setState(next);
  }
  return [state, set];
}
